import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { ActionIcon, Button, Divider, Select, Switch, Tooltip } from '@mantine/core';
import { PanelLeftClose, PanelRightClose, Plus, User, Users } from 'lucide-react';
import { FC, ReactNode, useEffect } from 'react';

import CircularLoader from '@/components/CircularLoader.tsx';
import DraggableComponent from '@/components/DraggableComponent.tsx';
import PracticeSelect from '@/components/Filtering/PracticeSelect.tsx';
import RoleSelect from '@/components/Filtering/RoleSelect.tsx';
import TextFilter from '@/components/Filtering/TextFilter.tsx';
import { Page } from '@/components/Page.tsx';
import PageTitleBar from '@/components/PageTitleBar.tsx';
import TaskCard from '@/components/TaskView/TaskCard.tsx';
import Column from '@/components/drag-and-drop/Column.tsx';

import { useResolvedPracticeSelection, useResolvedRoleSelection } from '@/hooks/use-task-filter-selection.ts';
import usePvsPractitioners from '@/hooks/usePvsPractitioners.ts';
import { usePvsUserRoles } from '@/hooks/usePvsUserRoles.ts';
import useTasks from '@/hooks/useTasks.ts';

import { NotificationType } from '@/models/Notification.ts';
import { TaskSortByOption, sort_by_opts } from '@/models/SortByOption.ts';
import { TaskModalMode } from '@/models/TaskModalMode.ts';
import { DCTaskStatus, EnrichedTask, task_status_de_translation } from '@/models/doc-cirrus/DCTask.ts';

import { showLoadingNotification, updateNotification } from '@/services/notificationService.ts';
import { updateTaskAndEnrich } from '@/services/tasksService.ts';

import { useTaskViewStore } from '@/stores/taskViewStore.ts';

import { openTaskAssignModal } from '@/utils/modalUtils.tsx';
import { prepareKanbanMove } from '@/utils/roomViewUtils.ts/dragAndDrop.ts';
import { cn } from '@/utils/styleUtils.ts';
import { filterTasks, sortTasks } from '@/utils/task-utils.ts';
import { openTaskModal } from '@/utils/task-utils.tsx';

const TASKS_PER_PAGE = 20;

const TaskViewPage: FC = () => {
    const { pvs_user_roles } = usePvsUserRoles();
    const {
        is_completed_column_shown,
        is_only_me_selected,
        search_text,
        sort_by,
        new_tasks_page,
        assigned_tasks_page,
        done_tasks_page,
        dragging_task,
    } = useTaskViewStore();
    const { open_tasks, done_tasks, task_types, is_loading_open, updateOrCreateTask } = useTasks();
    const { instance_users_map } = usePvsPractitioners();

    // List of pvs user ids attached to the logged in user
    const auth_user_pvs_ids = new Set(pvs_user_roles.map((role) => role.pvs_user_id));

    // Same resolution as the dropdowns: unset → all, explicit empty → "none" bucket.
    const { values: selected_roles_by_location } = useResolvedRoleSelection();
    const { values: selected_practices_by_location } = useResolvedPracticeSelection();

    const filtered_open_tasks: EnrichedTask[] = filterTasks(
        open_tasks,
        selected_roles_by_location,
        selected_practices_by_location,
        is_only_me_selected,
        auth_user_pvs_ids,
        search_text
    );

    const filtered_new_tasks: EnrichedTask[] = sortTasks(
        filtered_open_tasks.filter((task) => task.status === DCTaskStatus.NEW),
        sort_by
    );

    const filtered_assigned_tasks: EnrichedTask[] = sortTasks(
        filtered_open_tasks.filter((task) => task.status === DCTaskStatus.ASSIGNED),
        sort_by
    );

    const filtered_done_tasks: EnrichedTask[] = sortTasks(
        filterTasks(
            done_tasks,
            selected_roles_by_location,
            selected_practices_by_location,
            is_only_me_selected,
            auth_user_pvs_ids,
            search_text
        ),
        sort_by
    );

    // Sliced tasks for infinite scrolling (only show first N pages)
    const displayed_new_tasks: EnrichedTask[] = filtered_new_tasks.slice(0, new_tasks_page * TASKS_PER_PAGE);
    const displayed_assigned_tasks: EnrichedTask[] = filtered_assigned_tasks.slice(
        0,
        assigned_tasks_page * TASKS_PER_PAGE
    );
    const displayed_done_tasks: EnrichedTask[] = filtered_done_tasks.slice(0, done_tasks_page * TASKS_PER_PAGE);

    // Columns computed from current SWR data (includes optimistic updates)
    const computed_columns = new Map([
        [
            DCTaskStatus.NEW,
            {
                id: DCTaskStatus.NEW,
                collapsed: false,
                items: displayed_new_tasks,
                type: DCTaskStatus.NEW,
                title: task_status_de_translation[DCTaskStatus.NEW].toUpperCase(),
            },
        ],
        [
            DCTaskStatus.ASSIGNED,
            {
                id: DCTaskStatus.ASSIGNED,
                collapsed: false,
                items: displayed_assigned_tasks,
                type: DCTaskStatus.ASSIGNED,
                title: task_status_de_translation[DCTaskStatus.ASSIGNED].toUpperCase(),
            },
        ],
        [
            DCTaskStatus.DONE,
            {
                id: DCTaskStatus.DONE,
                collapsed: false,
                items: displayed_done_tasks,
                type: DCTaskStatus.DONE,
                title: task_status_de_translation[DCTaskStatus.DONE].toUpperCase(),
            },
        ],
    ]);

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 20, // Drag only starts after moving the mouse distance in px
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 100,
                tolerance: 5,
            },
        })
    );

    const onCardClick = (task: EnrichedTask): void => {
        let task_list: EnrichedTask[] = [];
        switch (task.status) {
            case DCTaskStatus.DONE: {
                task_list = filtered_done_tasks;
                break;
            }
            default: {
                task_list = filtered_open_tasks.filter((item) => item.status === task.status);
                break;
            }
        }

        useTaskViewStore.setState({ task_list });
        openTaskModal({ task: task, mode: TaskModalMode.DETAILS });
    };

    const renderTask = (task: EnrichedTask): ReactNode => (
        <DraggableComponent id={task.id} key={task.id}>
            <TaskCard task={task} onClick={onCardClick} />
        </DraggableComponent>
    );

    // Load more functions for each column (using store actions)
    const loadMoreNewTasks = (): void => useTaskViewStore.setState({ new_tasks_page: new_tasks_page + 1 });
    const loadMoreAssignedTasks = (): void =>
        useTaskViewStore.setState({ assigned_tasks_page: assigned_tasks_page + 1 });
    const loadMoreDoneTasks = (): void => useTaskViewStore.setState({ done_tasks_page: done_tasks_page + 1 });

    const new_column: ReactNode = (
        <Column
            id={DCTaskStatus.NEW}
            title={`${task_status_de_translation[DCTaskStatus.NEW].toUpperCase()}`}
            fallbackText="Keine Aufgaben"
            classNames={{
                wrapper: is_completed_column_shown ? 'basis-2/6' : 'basis-1/2',
            }}
            infiniteScroll={{
                totalDataLength: filtered_new_tasks.length,
                dataLength: displayed_new_tasks.length,
                hasMore: displayed_new_tasks.length < filtered_new_tasks.length,
                loadMore: loadMoreNewTasks,
            }}
        >
            {displayed_new_tasks.map(renderTask)}
        </Column>
    );

    const assigned_column: ReactNode = (
        <Column
            id={DCTaskStatus.ASSIGNED}
            title={`${task_status_de_translation[DCTaskStatus.ASSIGNED].toUpperCase()}`}
            fallbackText="Keine Aufgaben"
            classNames={{
                wrapper: is_completed_column_shown ? 'basis-2/6' : 'basis-1/2',
            }}
            infiniteScroll={{
                totalDataLength: filtered_assigned_tasks.length,
                dataLength: displayed_assigned_tasks.length,
                hasMore: displayed_assigned_tasks.length < filtered_assigned_tasks.length,
                loadMore: loadMoreAssignedTasks,
            }}
        >
            {displayed_assigned_tasks.map(renderTask)}
        </Column>
    );

    const done_column: ReactNode = is_completed_column_shown ? (
        <Column
            id={DCTaskStatus.DONE}
            title={`${task_status_de_translation[DCTaskStatus.DONE].toUpperCase()}`}
            fallbackText="Keine Aufgaben"
            classNames={{
                wrapper: 'basis-2/6',
            }}
            infiniteScroll={{
                totalDataLength: filtered_done_tasks.length,
                dataLength: displayed_done_tasks.length,
                hasMore: displayed_done_tasks.length < filtered_done_tasks.length,
                loadMore: loadMoreDoneTasks,
            }}
        >
            {displayed_done_tasks.map(renderTask)}
        </Column>
    ) : undefined;

    // Reset pagination when filters or sort changes
    useEffect(() => {
        useTaskViewStore.setState({
            new_tasks_page: 1,
            assigned_tasks_page: 1,
            done_tasks_page: 1,
        });
    }, [selected_roles_by_location, selected_practices_by_location, is_only_me_selected, search_text, sort_by]);

    const updateTaskWithNotification = async (
        task: EnrichedTask,
        new_status: DCTaskStatus,
        employee_id?: string
    ): Promise<EnrichedTask | undefined> => {
        const notification_id = showLoadingNotification('Aktualisieren...', task.title);

        try {
            const new_task = { ...task, status: new_status };
            if (employee_id) new_task.employee_id = employee_id;

            const updated_task = await updateTaskAndEnrich(
                task.instance_id,
                task.id,
                new_task,
                task_types,
                instance_users_map
            );

            updateNotification(NotificationType.SUCCESS, {
                id: notification_id,
                title: updated_task.title,
                message: 'Aufgabe aktualisiert',
            });

            return updated_task;
        } catch {
            updateNotification(NotificationType.ERROR, {
                id: notification_id,
                message: 'Aufgabenaktualisierung fehlgeschlagen',
            });

            return undefined;
        }
    };

    const handleDragStart = (event: DragStartEvent): void => {
        const task = [...(computed_columns?.values() || [])]
            .flatMap((col) => col.items)
            .find((app) => app.id === event.active.id);

        useTaskViewStore.setState({ dragging_task: task });
    };

    const handleDragEnd = async (event: DragEndEvent): Promise<void> => {
        const task = dragging_task;
        // reset dragging item
        useTaskViewStore.setState({ dragging_task: undefined });

        if (!computed_columns || !task) return;

        // basic validation
        const kanban_move_data = prepareKanbanMove(event, computed_columns);
        if (!kanban_move_data) return;
        const { source_column, destination_column } = kanban_move_data;
        if (source_column.id === destination_column.id) return;

        try {
            if (destination_column.type === DCTaskStatus.ASSIGNED) {
                openTaskAssignModal(
                    task.instance_id,
                    async (employee_id) => {
                        await updateOrCreateTask(async () => {
                            const updated_task = await updateTaskWithNotification(
                                task,
                                destination_column.type,
                                employee_id
                            );
                            if (!updated_task) {
                                throw new Error('Failed to move task');
                            }
                            return updated_task;
                        });
                    },
                    () => {} // Cancelled - no action needed, SWR will handle reversion
                );
            } else {
                await updateOrCreateTask(async () => {
                    const updated_task = await updateTaskWithNotification(task, destination_column.type);
                    if (!updated_task) {
                        throw new Error('Failed to move task');
                    }
                    return updated_task;
                });
            }
        } catch {
            // updateOrCreateTask handles its own error reversion
        }
    };

    return (
        <Page className="gap-10 overflow-hidden p-4 pb-0 pt-10 xl:p-10 xl:pb-0">
            <PageTitleBar
                title="Aufgaben"
                ctaButton={
                    <Button
                        onClick={() => {
                            useTaskViewStore.setState({ task_list: [], show_task_list: false });
                            openTaskModal({ mode: TaskModalMode.EDITOR });
                        }}
                        leftSection={<Plus className="size-4" />}
                        classNames={{ label: 'overflow-visible' }}
                    >
                        Aufgabe erstellen
                    </Button>
                }
            />
            <div className="flex w-full items-center justify-between gap-3">
                <TextFilter
                    placeholder="Suchaufgaben..."
                    value={search_text}
                    onChange={(value) => useTaskViewStore.setState({ search_text: value })}
                />
                <RoleSelect />
                <PracticeSelect />
                <Select
                    allowDeselect={false}
                    data={sort_by_opts}
                    value={sort_by}
                    onChange={(value) => useTaskViewStore.setState({ sort_by: value as TaskSortByOption })}
                />
                <Tooltip label={is_only_me_selected ? 'Meine Aufgaben' : 'Alle'} position="top" refProp="rootRef">
                    <Switch
                        size="lg"
                        checked={is_only_me_selected}
                        onChange={() => {
                            useTaskViewStore.setState({
                                is_only_me_selected: !is_only_me_selected,
                            });
                        }}
                        onLabel={<User className="size-5" />}
                        offLabel={<Users className="size-5" />}
                        classNames={{
                            label: 'text-lg',
                            track: cn('bg-slate-100', is_only_me_selected && 'bg-green-300'),
                        }}
                    />
                </Tooltip>
                <Tooltip
                    label={is_completed_column_shown ? 'Erledigte Aufgaben ausblenden' : 'Erledigte Aufgaben anzeigen'}
                    withArrow
                >
                    <ActionIcon
                        variant="transparent"
                        onClick={() =>
                            useTaskViewStore.setState({ is_completed_column_shown: !is_completed_column_shown })
                        }
                    >
                        {is_completed_column_shown ? (
                            <PanelRightClose className="text-black" />
                        ) : (
                            <PanelLeftClose className="text-black" />
                        )}
                    </ActionIcon>
                </Tooltip>
            </div>
            <div className="flex min-h-0 w-full flex-grow">
                {is_loading_open ? (
                    <div className="flex h-full w-full items-center justify-center">
                        <CircularLoader />
                    </div>
                ) : (
                    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                        {new_column}
                        <Divider orientation="vertical" />
                        {assigned_column}
                        {is_completed_column_shown && (
                            <>
                                <Divider orientation="vertical" />
                                {done_column}
                            </>
                        )}
                        <DragOverlay modifiers={[snapCenterToCursor]}>
                            {dragging_task && (
                                <div className="opacity-60">
                                    <TaskCard task={dragging_task} onClick={() => {}} />
                                </div>
                            )}
                        </DragOverlay>
                    </DndContext>
                )}
            </div>
        </Page>
    );
};

export default TaskViewPage;
